/**
 * Centralized logging utility
 *
 * Features:
 * - Only logs in development mode
 * - Sends errors to Sentry in production
 * - Provides consistent logging interface
 * - Prevents sensitive data from leaking to console
 *
 * Usage:
 * ```typescript
 * import { logger } from '../utils/logger'
 *
 * logger.info('User created', { userId: 123 })
 * logger.error('Failed to save', error)
 * logger.warn('Rate limit approaching')
 * logger.debug('Detailed debugging info', data)
 * ```
 */

type LogLevel = 'info' | 'error' | 'warn' | 'debug'

interface LogData {
  [key: string]: any
}

class Logger {
  private isDevelopment(): boolean {
    return import.meta.env.MODE === 'development'
  }

  private isProduction(): boolean {
    return import.meta.env.MODE === 'production'
  }

  /**
   * Send error to Sentry in production
   */
  private sendToSentry(error: Error, context?: string, data?: LogData) {
    if (this.isProduction() && typeof Sentry !== 'undefined') {
      Sentry.captureException(error, {
        tags: context ? { context } : undefined,
        extra: data,
      })
    }
  }

  /**
   * Log informational messages
   * Only in development mode
   */
  info(message: string, data?: LogData): void {
    if (this.isDevelopment()) {
      console.log(`ℹ️ ${message}`, data || '')
    }
  }

  /**
   * Log errors
   * - Development: Logs to console
   * - Production: Sends to Sentry
   */
  error(message: string, error?: Error | unknown, data?: LogData): void {
    if (this.isDevelopment()) {
      console.error(`❌ ${message}`, error || '', data || '')
    }

    // Send to Sentry in production
    if (error instanceof Error) {
      this.sendToSentry(error, message, data)
    } else if (error) {
      this.sendToSentry(new Error(message), message, {
        ...data,
        originalError: error,
      })
    }
  }

  /**
   * Log warnings
   * Only in development mode
   */
  warn(message: string, data?: LogData): void {
    if (this.isDevelopment()) {
      console.warn(`⚠️ ${message}`, data || '')
    }
  }

  /**
   * Debug messages
   * Only in development mode
   */
  debug(message: string, data?: LogData): void {
    if (this.isDevelopment()) {
      console.debug(`🔍 ${message}`, data || '')
    }
  }

  /**
   * Group related logs together
   * Only in development mode
   */
  group(label: string, callback: () => void): void {
    if (this.isDevelopment()) {
      console.group(label)
      callback()
      console.groupEnd()
    }
  }

  /**
   * Log a table (useful for arrays/objects)
   * Only in development mode
   */
  table(data: any): void {
    if (this.isDevelopment()) {
      console.table(data)
    }
  }

  /**
   * Time operations
   * Only in development mode
   */
  time(label: string): void {
    if (this.isDevelopment()) {
      console.time(label)
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment()) {
      console.timeEnd(label)
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Export for easier importing in components
export default logger
